import React, { useEffect, useState, useRef } from 'react';
import { Button, ButtonDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { Registerer, SessionState, UserAgent } from 'sip.js';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toZonedTime } from 'date-fns-tz';
import { startOfDay } from 'date-fns';
import * as Icon from 'react-feather';
import PropTypes from 'prop-types';

// COMPONENT IMPORTS
import config from '../../config';
import Timer from '../../utils/Timer';
import { socket } from '../../config/Socket';
import DtmfScreen from './dialScreens/DtmfScreen';
import DialPadScreen from './dialScreens/DialPadScreen';
import TransferScreen from './dialScreens/TransferScreen';
import ringtoneMp3 from '../../assets/sound/ringtone.mp3';
import { useCallingData } from '../Context/CallingContext';
import DefaultLiveCall from './dialScreens/DefaultLiveCall';
import ConferenceScreen from './dialScreens/ConferenceScreen';
import UserTransferScreen from './dialScreens/UserTransferScreen';
import UserCallScreen from './dialScreens/UserCallScreen';
import IncomingCallScreen from './dialScreens/IncomingCallScreen';
import AgentTransferScreen from './dialScreens/AgentTransferScreen';
import ConferenceLiveScreen from './dialScreens/ConferenceLiveScreen';
import TransferHangupScreen from './dialScreens/TransferHangupScreen';
import { formatPayloadDate, getCurrentDateTime } from '../../utils/common';
import { isOnBreakNow, setBreakPending } from '../../utils/breakState';
import { clearStorageKeepTheme } from '../../utils/theme';
import { fetchCrmAndLand } from '../../utils/crmLanding';
import {
  clearActiveSession,
  getMediaLiveness,
  resetMediaLiveness,
  setActiveSession,
  setSoftphoneEnabled,
  subscribeSessionEnded
} from '../../utils/mediaLiveness';
import {
  DECISION,
  GUARD_STATE,
  HANGUP_HOLD_MAX_MS,
  HANGUP_HOLD_MS,
  STUCK_CALL_MS,
  decideEvent,
  guardStateForStatus,
  normalizeCallEvent
} from '../../utils/callEventGuard';
import { toast } from 'react-toastify';

// APIS IMPORTS
import { setCallingModalOpen, setSidebarType, useTimezone } from '../../store/slices/commonSlice';
import { authLogout, useUserDetails } from '../../store/slices/authSlice';
import { clearPersister } from '../../utils/common';
import {
  onSetCallStatus,
  onSetCallType,
  setBreakDetails,
  setCallDialSec,
  setCallEnd,
  setCallId,
  setCallNumber,
  setCurrentCallDetail,
  setIsDialNumber,
  setDialNumberType,
  setIsReDial,
  setScreenType,
  setSelectBreakType,
  useBreakDetails,
  useBreakType,
  useCallId,
  useCallingStatus,
  useCurrentCallDetail,
  useScreenType,
  useIsDialNumber,
  useDialNumberType,
  useDialSec,
  useIsReDial,
  useCallEnd,
  useHoldTime,
  setHoldTime,
  setHoldStartTime,
  useHoldStartTime,
  setSipStatus,
  useCallType,
  useSipStatus,
  useConferenceDetail,
  setConferenceDetail
} from '../../store/slices/dialSlice';
import {
  breaksListGet,
  crmInfoLog,
  manualCrmInfo,
  followUpAdd,
  handleConference,
  hangupCall,
  manualCall,
  manualDialing,
  pauseModeUpdate,
  progressiveCall,
  removeCrmDetails,
  selectBreak,
  setIdleCall,
  setPauseMode,
  setUnregisterCall,
  updateCrmInformation,
  useBreaksList,
  useCrmDetails,
  usePauseMode
} from '../../store/slices/callingSlice';
import MicrophonePermissionModal from '../Modals/MicrophonePermissionModal';
import SipConnectionModal from '../Modals/SipConnectionModal';
import { subscribeSipAlertRequest } from '../../utils/sipAlert';
import { playBeep } from '../../utils/audioUtils';

const CallingSidebar = ({ setIsProcessModalOpen, isMini = false }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const timezone = useTimezone();
  const today = toZonedTime(new Date(), timezone);
  const isEmbedded = window.self !== window.top;

  const {
    informationData,
    dispositionsData,
    fieldsData,
    followUp,
    isPauseMode,
    setFollowUp,
    setIsPauseMode,
    handleTimeReset,
    setMutedConference,
    setOnHoldConference,
    showMicModal,
    micPermission,
    requestMicrophonePermission
  } = useCallingData();
  const pauseMode = usePauseMode();
  const user = useUserDetails();
  const callStatus = useCallingStatus();
  const callId = useCallId();
  const breaksList = useBreaksList();
  const breakType = useBreakType();
  const screenType = useScreenType();
  const isDialNumber = useIsDialNumber();
  const dialNumberType = useDialNumberType();
  const breakDetails = useBreakDetails();
  const CurrentCallDetail = useCurrentCallDetail();
  const crmDetails = useCrmDetails();
  const dialSec = useDialSec();
  const isReDial = useIsReDial();
  const callEnd = useCallEnd();
  const holdTime = useHoldTime();
  const holdStartTime = useHoldStartTime();
  const callType = useCallType();
  const sipStatus = useSipStatus();
  const conferenceDetail = useConferenceDetail();

  const [isOpen, setIsOpen] = useState(false);
  const [isConVoxOpen, setIsConVoxOpen] = useState(false);
  const [number, setNumber] = useState('');
  const [duration, setDuration] = useState(0);
  const [wrapsec, setWrapsec] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);
  const [sipIssue, setSipIssue] = useState(null);
  const [isSipRetrying, setIsSipRetrying] = useState(false);

  const hasSipAlert =
    !isMini &&
    Number(user?.web_phone) === 0 &&
    !breakDetails &&
    !callStatus &&
    !showMicModal &&
    (!!sipIssue || isSipRetrying);
  const isSipAlertOpen = hasSipAlert && sipStatus !== 'Register';

  const intervalRef = useRef(null);
  const incomingInvitationRef = useRef(null);
  const reconnectInProgressRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef(null);
  const ringtoneRef = useRef(null);
  const updateCrmRef = useRef();
  const breakDetailsRef = useRef(breakDetails);
  const sipStatusRef = useRef(sipStatus);
  const callStatusRef = useRef(callStatus);
  const callIdRef = useRef(callId);
  const seenCallUuidsRef = useRef([]);
  const currentCallUuidsRef = useRef(new Set());
  const callGuardRef = useRef({ state: GUARD_STATE.IDLE });
  const pendingHangupRef = useRef(null);
  const handleMessageRef = useRef(null);
  const landedCallIdRef = useRef(null);
  const crmFetchInFlightRef = useRef(false);
  const progressiveNumberFilledRef = useRef(null);

  // Remember a live uuid so a later HANGUP for it is recognised as stale (capped at 200).
  const markCallUuidSeen = (uuid) => {
    if (!uuid) return;
    const seen = seenCallUuidsRef.current;
    if (seen.includes(uuid)) return;
    seen.push(uuid);
    if (seen.length > 200) seen.splice(0, seen.length - 200);
  };

  // Claim a uuid for the current call. All dial paths reach here via the `callId` effect.
  const markCallUuidCurrent = (uuid) => {
    if (!uuid) return;
    markCallUuidSeen(uuid);
    currentCallUuidsRef.current.add(uuid);
  };

  const clearPendingHangup = () => {
    const pending = pendingHangupRef.current;
    if (!pending) return;
    if (pending.timerId) clearTimeout(pending.timerId);
    if (pending.maxTimerId) clearTimeout(pending.maxTimerId);
    pendingHangupRef.current = null;
  };

  // Back to "no call". Runs on every call end — see the reconciliation effect below.
  const resetCallGuard = () => {
    clearPendingHangup();
    currentCallUuidsRef.current = new Set();
    callGuardRef.current = { state: GUARD_STATE.IDLE };
  };

  // Log non-accepted guard decisions to the backend so a complaint can be traced to its event.
  const logGuardEvent = (decision, detail) => {
    const payload = { tag: 'call-event-guard', decision, ...detail };
    console.log('[call-event-guard]', payload);
    getLogDetails({
      user_id: user?.user_id,
      message: 'call event guard',
      'socket.id': socket.id,
      message_data: payload
    });
  };

  // Apply a teardown we had withheld, through the same path a live event takes.
  const releasePendingHangup = (reason) => {
    const pending = pendingHangupRef.current;
    if (!pending) return;
    clearPendingHangup();
    logGuardEvent('held-released', { reason, uuid: pending.message?.origination_uuid });
    handleMessageRef.current?.({ message: pending.message, forceAccept: true });
  };

  // Hold an unattributable teardown instead of applying or dropping it. Released on media
  // end, on hold expiry with no live media, or at the hard cap (so it can never strand ON CALL).
  const holdHangup = (message) => {
    clearPendingHangup();
    const timerId = setTimeout(() => {
      if (getMediaLiveness() === 'live') return;
      releasePendingHangup('hold-expired');
    }, HANGUP_HOLD_MS);
    const maxTimerId = setTimeout(() => releasePendingHangup('hold-cap'), HANGUP_HOLD_MAX_MS);
    pendingHangupRef.current = { message, timerId, maxTimerId };
  };

  // Keep refs in sync with the latest Redux state on every render
  useEffect(() => {
    breakDetailsRef.current = breakDetails;
  }, [breakDetails]);
  useEffect(() => {
    sipStatusRef.current = sipStatus;
  }, [sipStatus]);
  useEffect(() => {
    callStatusRef.current = callStatus;
  }, [callStatus]);
  useEffect(() => {
    callIdRef.current = callId;
    markCallUuidCurrent(callId);
  }, [callId]);

  // Anti-latch: every wrapup exit ends with `callStatus` going null, so reset the guard on
  // that one condition instead of listing exits. One-way: never overridden mid-call.
  useEffect(() => {
    if (isMini) return;
    if (callStatus) return;
    if (callGuardRef.current.state === GUARD_STATE.IDLE && !pendingHangupRef.current) return;
    resetCallGuard();
  }, [callStatus, isMini]);

  // `web_phone === 0` enables the browser softphone; on an external phone there is no
  // session to read, so media liveness stays 'unknown' and never gates anything.
  useEffect(() => {
    setSoftphoneEnabled(Number(user?.web_phone) === 0);
  }, [user?.web_phone]);

  // Media ended — release any held teardown now (the timers are only a fallback).
  useEffect(() => {
    if (isMini) return;
    return subscribeSessionEnded(() => releasePendingHangup('media-ended'));
  }, [isMini]);

  // A reload resets refs but not an external-phone call, so seed the guard from the
  // persisted status; an IDLE guard would let any teardown through.
  useEffect(() => {
    if (isMini) return;
    if (!callStatusRef.current) return;
    callGuardRef.current = { state: guardStateForStatus(callStatusRef.current) };
  }, []);

  // Stuck-call watchdog: if media is gone and no teardown ever arrived, the agent is
  // parked on a dead call. Softphone agents only, and only from IN_CALL (ringing has no media).
  useEffect(() => {
    if (isMini || isEmbedded) return;
    if (callStatus !== 'ON CALL') return;

    // Act only on media we saw come UP and then go away — predictive shows ON CALL before
    // the INVITE lands, so "no media yet" must never count as "call ended".
    let sawLive = false;
    let endedSince = null;
    const tick = () => {
      if (isOnBreakNow() || callGuardRef.current.state !== GUARD_STATE.IN_CALL) {
        endedSince = null;
        return;
      }
      const liveness = getMediaLiveness();
      if (liveness === 'live') {
        sawLive = true;
        endedSince = null;
        return;
      }
      if (!sawLive || liveness !== 'ended') {
        endedSince = null;
        return;
      }
      if (endedSince === null) {
        endedSince = Date.now();
        return;
      }
      if (Date.now() - endedSince < STUCK_CALL_MS) return;

      logGuardEvent('stuck-call-recovered', {
        callId: callIdRef.current,
        heldTeardown: !!pendingHangupRef.current
      });
      clearPendingHangup();
      callGuardRef.current = { state: GUARD_STATE.WRAPUP };
      dispatch(onSetCallStatus('WRAPUP'));
    };

    const watchdog = setInterval(tick, 5000);
    return () => clearInterval(watchdog);
  }, [callStatus, isMini, isEmbedded]);

  const onLogout = async () => {
    try {
      await dispatch(authLogout()).unwrap();
      socket.emit('user_logout', {
        user_id: user?.user_id,
        process_id: user?.process_details?.process_id,
        company_id: user?.companyId,
        type: user?.type
      });
      console.log('[logout] user_logout emitted', { connected: socket.connected, socket_id: socket.id });
      socket.disconnect();
      clearPersister(dispatch);
      // Module-level state — would otherwise leak into the next agent's session.
      resetMediaLiveness();
      resetCallGuard();
      window.__SIP_MANUAL_LOGOUT__ = true;
      clearStorageKeepTheme();
      localStorage.removeItem('timerDuration');
      localStorage.removeItem('timerStartTime');
      navigate('/login');
    } catch (error) {
      console.log('error', error);
    }
  };

  const userAgentRef = useRef(null);
  const registererRef = useRef(null);
  const registerRetryTimeoutRef = useRef(null);
  const registerRetryAttemptsRef = useRef(0);

  window.__SIP_MANUAL_LOGOUT__ = false;

  const clearRegisterRetry = () => {
    if (registerRetryTimeoutRef.current) {
      clearTimeout(registerRetryTimeoutRef.current);
      registerRetryTimeoutRef.current = null;
    }
  };

  // Record a failure for the connection alert, ignoring intentional teardowns (break,
  // logout, web_phone off), which end in Unregistered too.
  const flagSipIssue = (reason) => {
    if (window.__SIP_MANUAL_LOGOUT__ || isOnBreakNow() || !userAgentRef.current) return;
    setSipIssue(reason);
    setIsSipRetrying(false);
  };

  // SIP.js never retries a rejected REGISTER (except 423), so a transient reject such as
  // a 503 during a FreeSWITCH restart would otherwise leave the phone un-registered.
  const scheduleRegisterRetry = (retryAfterSeconds) => {
    if (window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) return;
    clearRegisterRetry();
    const attempt = registerRetryAttemptsRef.current;
    registerRetryAttemptsRef.current = attempt + 1;
    // Honour the server's Retry-After when it sends one, otherwise back off.
    const delay = Number(retryAfterSeconds)
      ? Number(retryAfterSeconds) * 1000
      : Math.min(30000, 2000 * 2 ** attempt);
    console.log(`REGISTER retry scheduled in ${delay / 1000}s`);
    registerRetryTimeoutRef.current = setTimeout(() => {
      registerRetryTimeoutRef.current = null;
      if (window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) return;
      registerUA();
    }, delay);
  };

  // Per-response delegates: `stateChange` stays silent when an already-Registered
  // registerer re-registers, which would leave the header stuck on "Connecting".
  const registerRequestDelegate = {
    onAccept: () => {
      console.log('✅ SIP Registered Successfully');
      registerRetryAttemptsRef.current = 0;
      clearRegisterRetry();
      dispatch(setSipStatus('Register'));
    },
    onReject: (response) => {
      const statusCode = response?.message?.statusCode;
      console.warn(`❌ REGISTER rejected with ${statusCode}`);
      dispatch(setSipStatus('Un-Register'));
      flagSipIssue('unregistered');
      scheduleRegisterRetry(registererRef.current?.retryAfter);
    }
  };

  // const remoteStream = new MediaStream();

  // Publish the live media session for the wrapup guards. Every call type arrives as an
  // inbound INVITE; tracked separately because `incomingInvitationRef` clears on answer.
  const trackSipSession = (session) => {
    if (!session?.stateChange) return;
    session.stateChange.addListener((state) => {
      if (state === SessionState.Established) setActiveSession(session);
      if (state === SessionState.Terminated) clearActiveSession(session);
    });
  };

  const connectSIP = async () => {
    try {
      window.__SIP_MANUAL_LOGOUT__ = false;
      const sipUri = UserAgent.makeURI(
        `sip:${user.username}@${user.domain}:${process.env.REACT_APP_SIP_PORT}`
      );
      if (!sipUri) {
        console.error('Invalid SIP URI');
        return;
      }

      const userAgentOptions = {
        uri: sipUri,
        authorizationPassword: user?.password,
        authorizationUsername: user?.username,
        transportOptions: {
          server: config.WSS_URL,
          keepAliveInterval: 20,
          keepAliveDebounce: 10,
          connectionTimeout: 10
        }
        // sessionDescriptionHandlerFactoryOptions: {
        //   peerConnectionConfiguration: {
        //     iceServers: [
        //       { urls: 'stun:stun.l.google.com:19302' },
        //       { urls: 'stun:stun1.l.google.com:19302' }
        //     ]
        //   }
        // }
      };
      dispatch(setSipStatus('Connecting'));
      console.log('Creating UserAgent...');
      userAgentRef.current = new UserAgent(userAgentOptions);

      console.log('Starting UserAgent...');
      await userAgentRef.current.start();
      userAgentRef.current.transport.stateChange.addListener((state) => {
        if (state === 'Disconnected' && !window.__SIP_MANUAL_LOGOUT__) {
          reconnectUA();
        }
      });

      console.log('Registering UserAgent...');
      registererRef.current = new Registerer(userAgentRef.current);

      registererRef.current.stateChange.addListener((state) => {
        switch (state) {
          case 'Registered':
            console.log('✅ SIP Registered Successfully');
            dispatch(setSipStatus('Register'));
            break;

          case 'Unregistered':
            console.log('❌ SIP Unregistered');
            dispatch(setSipStatus('Un-Register'));
            flagSipIssue('unregistered');
            break;

          case 'Terminated':
            console.log('⚠️ SIP Registration Terminated');
            dispatch(setSipStatus('Un-Register'));
            break;
        }
      });

      await registererRef.current.register({ requestDelegate: registerRequestDelegate });

      console.log('UserAgent registered and ready for calls.');
      userAgentRef.current.delegate = {
        onInvite: async (invitation) => {
          console.log('Incoming call...');
          // stop() on break is not awaited, so an INVITE can still arrive — reject it.
          if (isOnBreakNow()) {
            console.log('Rejecting incoming call — agent is on break');
            try {
              await invitation.reject();
            } catch (err) {
              console.error('Error rejecting call while on break:', err);
            }
            return;
          }
          incomingInvitationRef.current = invitation;
          trackSipSession(invitation);
          const userProcessType = user?.process_details?.type;
          if (
            process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
            (userProcessType === 'InboundAndManual' ||
              userProcessType === 'BlendedAndPredictive' ||
              userProcessType === 'BlendedAndPreview' ||
              userProcessType === 'BlendedAndProgressive')
          ) {
            playRingtone();

            invitation.stateChange.addListener((state) => {
              if (state === SessionState.Terminated) {
                stopRingtone();
                incomingInvitationRef.current = null;
              }
            });
          } else {
            try {
              await invitation.accept({
                sessionDescriptionHandlerOptions: {
                  constraints: {
                    audio: true,
                    video: false
                  }
                }
              });
              setupRemoteMedia(invitation);
              console.log('Call answered!');
            } catch (err) {
              console.error('Error answering call:', err);
            }
          }
        }
      };
    } catch (error) {
      console.error('SIP Connection Error:', error);
      flagSipIssue('unreachable');
    }
  };

  const answerCall = async () => {
    const invitation = incomingInvitationRef.current;
    if (!invitation) return;
    stopRingtone();

    try {
      await invitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false
          }
        }
      });
      setupRemoteMedia(invitation);
      console.log('Incoming call answered!');
      incomingInvitationRef.current = null;
    } catch (err) {
      incomingInvitationRef.current = null;
      console.error('Error answering Incoming call:', err);
    }
  };

  const registerUA = async () => {
    if (!userAgentRef.current) return;

    try {
      if (!registererRef.current || registererRef.current.state === 'Terminated') {
        registererRef.current = new Registerer(userAgentRef.current);
        registererRef.current.stateChange.addListener((state) => {
          switch (state) {
            case 'Registered':
              console.log('✅ SIP Registered Successfully');
              dispatch(setSipStatus('Register'));
              break;
            case 'Unregistered':
              console.log('❌ SIP Unregistered');
              dispatch(setSipStatus('Un-Register'));
              flagSipIssue('unregistered');
              break;
            case 'Terminated':
              console.log('⚠️ SIP Registration Terminated');
              dispatch(setSipStatus('Un-Register'));
              break;
          }
        });
      }

      // register() resolves once SENT; the outcome arrives via registerRequestDelegate.
      registererRef.current
        .register({ requestDelegate: registerRequestDelegate })
        .then(async () => {
          console.log('UA Register request sent ...');
        })
        .catch(async (error) => {
          console.error('Failed to send REGISTER ==> ', error);
          scheduleRegisterRetry();
        });
    } catch (error) {
      console.error('CATCH FOUND -  registerUA ==> ', error);
    }
  };

  const reconnectUA = async () => {
    if (reconnectInProgressRef.current || window.__SIP_MANUAL_LOGOUT__ || !userAgentRef.current) {
      return false;
    }
    reconnectInProgressRef.current = true;
    try {
      console.log('Connecting------------->');
      await userAgentRef.current.reconnect();
      dispatch(setSipStatus('Connecting'));
      await registerUA();
      // Connected again — clear any pending retry and reset the backoff.
      reconnectAttemptsRef.current = 0;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    } catch (error) {
      console.error('UA Reconnection error ==> ', error);
      // The network can stay down long after sleep/idle, so keep retrying with a capped backoff.
      if (!window.__SIP_MANUAL_LOGOUT__ && userAgentRef.current) {
        flagSipIssue('unreachable');
        const attempt = reconnectAttemptsRef.current;
        const delay = Math.min(30000, 2000 * 2 ** attempt);
        reconnectAttemptsRef.current = attempt + 1;
        if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectTimeoutRef.current = null;
          reconnectUA();
        }, delay);
      }
    } finally {
      reconnectInProgressRef.current = false;
    }
  };

  // "Reconnect now" on the connection alert, for drops auto-recovery misses (e.g. a rejected
  // registration refresh). Reuses the auto-recovery paths, so the switch sees nothing new.
  const retrySipRegistration = async () => {
    if (window.__SIP_MANUAL_LOGOUT__ || isOnBreakNow() || Number(user.web_phone) !== 0) return;
    setIsSipRetrying(true);
    dispatch(setSipStatus('Connecting'));
    // Start both backoffs over — the agent is asking for an attempt now, not in 30s.
    reconnectAttemptsRef.current = 0;
    registerRetryAttemptsRef.current = 0;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    clearRegisterRetry();

    const ua = userAgentRef.current;
    // A UA whose start() failed has no onInvite delegate — rebuild it rather than register a
    // phone that refuses calls. Null the ref first so its disconnect can't trigger reconnectUA.
    if (!ua || !ua.delegate?.onInvite) {
      if (ua) {
        userAgentRef.current = null;
        registererRef.current = null;
        await ua.stop().catch((error) => console.error('Stopping stale UserAgent failed', error));
        if (window.__SIP_MANUAL_LOGOUT__ || isOnBreakNow()) return;
      }
      await connectSIP();
      return;
    }
    if (!ua.isConnected()) {
      await reconnectUA();
      return;
    }
    await registerUA();
  };

  // const setupRemoteMedia = (mediaSession) => {
  //   const mediaElement = document.getElementById('mediaElement');
  //   try {
  //     mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
  //       if (receiver.track) {
  //         remoteStream.addTrack(receiver.track);
  //         mediaElement.srcObject = remoteStream;
  //         mediaElement.play();
  //       }
  //     });
  //   } catch (error) {
  //     console.log('Media audio session error - ', error);
  //   }
  // };

  const setupRemoteMedia = (mediaSession) => {
    const mediaElement = document.getElementById('mediaElement');
    if (!mediaElement) return;

    try {
      mediaElement.pause();
      mediaElement.srcObject = null;

      const stream = new MediaStream();

      mediaSession.sessionDescriptionHandler.peerConnection.getReceivers().forEach((receiver) => {
        if (receiver.track && receiver.track.kind === 'audio') {
          stream.addTrack(receiver.track);
        }
      });

      mediaElement.srcObject = stream;

      mediaElement.play().catch(() => {
        console.warn('Audio play interrupted or blocked by browser');
      });
    } catch (error) {
      console.log('Media audio session error - ', error);
    }
  };

  const playRingtone = () => {
    if (!ringtoneRef.current) return;
    if (!ringtoneRef.current.paused) return;

    ringtoneRef.current.play().catch(() => console.warn('Ringtone blocked'));
  };

  const stopRingtone = () => {
    if (!ringtoneRef.current) return;
    ringtoneRef.current.pause();
    ringtoneRef.current.currentTime = 0;
  };
  const isRingtonePlaying = () => !!ringtoneRef.current && !ringtoneRef.current.paused;

  useEffect(() => {
    if (isMini) return;
    ringtoneRef.current = new Audio(ringtoneMp3);
    ringtoneRef.current.loop = true;

    return () => {
      ringtoneRef.current?.pause();
      ringtoneRef.current = null;
    };
  }, [isMini]);

  useEffect(() => {
    if (isMini) return;
    const handleOnline = async () => {
      if (Number(user.web_phone) === 0) {
        reconnectAttemptsRef.current = 0;
        reconnectUA();
      }
    };

    const handleOffline = () => {
      if (Number(user.web_phone) === 0) {
        dispatch(setSipStatus('Un-Register'));
        flagSipIssue('offline');
        // A toast, not alert(): alert() froze the tab on every network dip.
        toast.error('Please check your connection');
      }
    };

    // Chrome often skips `online` after sleep, so also re-check on visibility — but only
    // reconnect when the transport is down, never disturb a healthy registration.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      if (Number(user.web_phone) !== 0 || breakDetails) return;
      const transportState = userAgentRef.current?.transport?.state;
      if (transportState && transportState !== 'Connected') {
        reconnectAttemptsRef.current = 0;
        reconnectUA();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [user.web_phone, isMini, breakDetails]);

  useEffect(() => {
    if (isMini) return;
    if (Number(user.web_phone) === 0 && !breakDetails) {
      reconnectInProgressRef.current = false;
      reconnectAttemptsRef.current = 0;
      Object.defineProperty(window, 'isSecureContext', { get: () => true });

      if (!userAgentRef.current) {
        connectSIP();
      } else {
        reconnectUA();
      }
    } else {
      reconnectInProgressRef.current = true;
      // SIP is off on purpose (break / web_phone off) — cancel any pending reconnect retry.
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      clearRegisterRetry();
      dispatch(setSipStatus('Un-Register'));
      console.log('SIP registration is disabled due to web_phone flag.');
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      clearRegisterRetry();
      if (userAgentRef.current) {
        dispatch(setSipStatus('Un-Register'));
        console.log('Stopping UserAgent...');
        userAgentRef.current.stop();
        userAgentRef.current = null;
      }
    };
  }, [user.web_phone, breakDetails, isMini]);

  // Break / web_phone off tears SIP down on purpose, so drop any earlier failure.
  useEffect(() => {
    if (isMini) return;
    setSipIssue(null);
    setIsSipRetrying(false);
  }, [user.web_phone, breakDetails, isMini]);

  // Registered again (Reconnect or auto-recovery) — close the alert and reset.
  useEffect(() => {
    if (isMini || sipStatus !== 'Register') return;
    if (hasSipAlert) toast.success(t('sipAlertReconnectedToast'));
    setSipIssue(null);
    setIsSipRetrying(false);
  }, [sipStatus, isMini]);

  // Header status click opens the alert even with no recorded failure (e.g. after a reload).
  useEffect(() => {
    if (isMini) return;
    return subscribeSipAlertRequest(() => {
      if (sipStatusRef.current === 'Register') return;
      setSipIssue((current) => current || 'unregistered');
    });
  }, [isMini]);

  useEffect(() => {
    if (isMini) return;
    let timerInterval;
    // No wrapup clock while on break — there is no call to wrap up.
    if (callStatus === 'WRAPUP' && !breakDetails) {
      timerInterval = setInterval(() => {
        setWrapsec((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => {
      clearInterval(timerInterval);
    };
  }, [callStatus, breakDetails, isMini]);

  useEffect(() => {
    if (isMini) return;
    // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    // const isAuto = autoWrapUpZero && wrapsec > autoWrapUp;
    const isAuto = wrapsec > autoWrapUp;
    // Never AUTO-submit while media is still live: that is a false WRAPUP, and it would
    // write AUTOWRAP on a call in progress. 'unknown' (external phone) must not block.
    if (isAuto && getMediaLiveness() === 'live') {
      // Effect runs every second — log only the first tick past the threshold.
      if (wrapsec === autoWrapUp + 1) {
        logGuardEvent('auto-submit-blocked', { source: 'sidebar', wrapsec });
      }
      return;
    }
    // No auto-submit when embedded (the host app owns the CRM) or on break (no live call).
    if (
      (isAuto || callEnd) &&
      !isLoading &&
      !isOnBreakNow() &&
      location.pathname !== '/crm' &&
      dialSec !== null &&
      !isEmbedded
    ) {
      updateCrmRef.current(isAuto);
    }
  }, [wrapsec, location.pathname, callEnd, isMini]);

  useEffect(() => {
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (callStatus === 'WRAPUP' && autoWrapUp > 10 && location.pathname !== '/crm') {
      if (autoWrapUp - wrapsec === 5) {
        const needsDispo = dispoRequired && !dispositionsData?.dispositioncode;
        const needsSubDispo = subDispoRequired && !dispositionsData?.subdispositioncode;
        const needsSubSubDispo = subSubDispoRequired && !dispositionsData?.subsubdispositioncode;

        if (needsDispo || needsSubDispo || needsSubSubDispo) {
          toast.warning('Please fill the required disposition fields. 5 seconds remaining.');
        }
      }
    }
  }, [wrapsec, callStatus, location.pathname]);

  const onPauseModeChange = async (pause_mode) => {
    try {
      const payload = { user_id: user?.id, pause_mode: pause_mode };
      const response = await dispatch(pauseModeUpdate(payload)).unwrap();
      if (response && response?.success) {
        dispatch(setPauseMode(payload?.pause_mode));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  const handleConferenceMode = async () => {
    try {
      const payload = {
        conf_id: 'all',
        type: 'hangup',
        conf_name: conferenceDetail?.conference_name
      };
      await dispatch(handleConference(payload)).unwrap();
      dispatch(setConferenceDetail(null));
      setMutedConference([]);
      setOnHoldConference([]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const updateCrm = async (isAuto = false) => {
    setIsLoading(true);
    const dispoRequired = user?.process_details?.is_dispo_required === 'yes';
    const subDispoRequired = user?.process_details?.is_sub_dispo_required === 'yes';
    const subSubDispoRequired = user?.process_details?.is_sub_sub_dispo_required === 'yes';

    if (!isAuto && !isReDial) {
      if (dispoRequired && !dispositionsData?.dispositioncode) {
        toast.error('Please select a disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subDispoRequired && !dispositionsData?.subdispositioncode) {
        toast.error('Please select a sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
      if (subSubDispoRequired && !dispositionsData?.subsubdispositioncode) {
        toast.error('Please select a sub-sub disposition');
        setIsLoading(false);
        dispatch(setCallEnd(false));
        return;
      }
    }

    if (conferenceDetail && conferenceDetail?.details?.length > 0) {
      handleConferenceMode();
    }
    try {
      // const autoWrapUpZero = String(user?.process_details?.autoWrapup) === '0';
      const autoWrapup = Number(user?.process_details?.autoWrapup) || 10;

      let hold_time = 0;

      if (holdStartTime !== null) {
        const holdDuration = Math.floor((Date.now() - holdStartTime) / 1000);
        hold_time = holdTime + holdDuration;
      } else {
        hold_time = holdTime;
      }

      const payload = {
        data: [
          {
            crmDetails: { ...informationData }
          },
          {
            fields: fieldsData?.fields ? fieldsData?.fields : []
          },
          {
            dispositions: dispositionsData?.dispositioncode
              ? { ...dispositionsData }
              : { dispositioncode: 'AUTOWRAP' }
          },
          {
            unique_id: crmDetails?.[4]?.unique_id,
            dialsec: callType === 'predictive' || callType === 'incoming' ? 0 : dialSec,
            hold_time: hold_time,
            wrapsec: wrapsec,
            // auto_wrapup: !autoWrapUpZero ? autoWrapup <= wrapsec : false
            auto_wrapup: autoWrapup <= wrapsec
          }
        ]
      };
      if (followUp?.isFollowUp === 'yes') {
        dispatch(
          followUpAdd({
            phone_number: crmDetails?.[1]?.crmDetails?.primaryNumber,
            crm_id: crmDetails?.[1]?.crmDetails?.Crms_id,
            date_and_time: formatPayloadDate(followUp?.date_and_time)
          })
        ).unwrap();
      }
      if (isPauseMode) {
        onPauseModeChange(1);
      }
      const data_message = {
        user_id: user.user_id,
        payload: payload,
        message: 'update Crm Information'
      };
      getLogDetails(data_message);
      const response = await dispatch(updateCrmInformation(payload)).unwrap();
      if (response) {
        const REDIAL_DELAY_MS = 4000;
        const NORMAL_DELAY_MS = 500;
        const postUpdateDelay = isReDial ? REDIAL_DELAY_MS : NORMAL_DELAY_MS;

        const responseTimer = setTimeout(() => {
          if (isReDial) {
            dispatch(setIsDialNumber(crmDetails?.[1]?.crmDetails?.primaryNumber));
          }
          setWrapsec(0);
          dispatch(setCallId(null));
          dispatch(setCallingModalOpen(user?.manualcall ? true : false));
          dispatch(setCallDialSec(null));
          dispatch(setCurrentCallDetail(null));
          dispatch(setSidebarType('main'));
          dispatch(setCallEnd(false));
          handleTimeReset();
          dispatch(onSetCallStatus(null));
          dispatch(onSetCallType(null));
          if (location.pathname !== '/chat') {
            navigate('/dashboard');
          }
          setIsLoading(false);
          dispatch(setHoldTime(0));
          dispatch(setHoldStartTime(null));
          setFollowUp({
            date_and_time: startOfDay(today),
            isFollowUp: 'no'
          });
          setIsPauseMode(false);
        }, postUpdateDelay);

        // const removeTimer = setTimeout(() => {
        //   dispatch(removeCrmDetails());
        // }, 600);

        return () => {
          clearTimeout(responseTimer);
          // clearTimeout(removeTimer);
        };
      }
    } catch (error) {
      setIsLoading(false);
      const data_message = {
        user_id: user.user_id,
        payload: error?.message,
        message: 'update Crm Information error'
      };
      getLogDetails(data_message);
      console.log('error', error);
    }
  };

  updateCrmRef.current = updateCrm;

  // Clear call state without CRM submit or navigation — for calls ending during a break.
  const resetCallState = () => {
    setWrapsec(0);
    dispatch(setCallId(null));
    dispatch(setCurrentCallDetail(null));
    dispatch(setCallDialSec(null));
    dispatch(setCallEnd(false));
    dispatch(onSetCallStatus(null));
    dispatch(onSetCallType(null));
    dispatch(setScreenType(''));
    dispatch(setHoldTime(0));
    dispatch(setHoldStartTime(null));
    dispatch(setSidebarType('main'));
    incomingInvitationRef.current = null;
    handleTimeReset();
  };

  // Embedded: only hang up. The host app owns the CRM, and state resets on the backend's
  // `call_hangup` event once the call is actually torn down.
  const onIframeHangup = async () => {
    try {
      if (callId) {
        await dispatch(hangupCall({ call_uuid: callId })).unwrap();
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isMini) return;
    if (!isEmbedded) return;
    if (!callEnd) return;
    onIframeHangup();
  }, [callEnd, isMini]);

  // `call_hangup` (sent once the call is fully torn down) is the single cue to reset state.
  useEffect(() => {
    if (!isEmbedded) return;

    const handleCallHangup = () => {
      console.log('call_hangup socket');
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setCallEnd(false));
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      dispatch(setScreenType(''));
      handleTimeReset();
      incomingInvitationRef.current = null;
      dispatch(setSidebarType('main'));
      // The resets above must always run (only teardown signal when embedded); only the
      // navigation is skipped on break so the agent isn't pulled off the break UI.
      if (isOnBreakNow()) return;
      navigate('/dashboard');
    };

    socket.on('call_hangup', handleCallHangup);
    return () => {
      socket.off('call_hangup', handleCallHangup);
    };
  }, [isEmbedded, user]);

  useEffect(() => {
    if (callStatus === 'WRAPUP' || !callStatus) {
      dispatch(setScreenType(''));
      setNumber('');
      dispatch(setCallNumber(''));
      setWrapsec(0);
    }
  }, [callStatus, breakDetails]);

  // Backstop: no call UI during a break — clears anything that slipped in just before the
  // break committed or was rehydrated by a reload taken during a break.
  useEffect(() => {
    if (isMini) return;
    if (!breakDetails) return;
    // Route first: call state may already be cleared elsewhere, leaving a dead CRM route.
    if (location.pathname === '/crm' || location.pathname === '/call-in-progress') {
      navigate('/dashboard');
    }
    if (!callStatus && !screenType) return;
    resetCallState();
  }, [breakDetails, callStatus, screenType, isMini, location.pathname]);

  useEffect(() => {
    if (isMini) return;
    socket.emit('identify', { user_id: user.user_id, type: user?.type });

    const handleServerRestart = ({ message }) => {
      console.log('server_restart-------->', message);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    // `forceAccept` marks the release of a held teardown, so it is not held again.
    const handleMessage = ({ message, forceAccept = false }) => {
      console.log('socket msg-->', message);
      console.log('socket id-->', socket.id);
      const data_message = {
        user_id: user.user_id,
        message: 'socket msg',
        'socket.id': socket.id,
        message_data: message
      };
      getLogDetails(data_message);
      // Ignore call-status messages on break. `isOnBreakNow()` latches on the break click,
      // covering the window before `breakDetails` lands where a HANGUP became WRAPUP + CRM.
      if (
        isOnBreakNow() ||
        (sipStatusRef.current === 'Un-Register' && Number(user.web_phone) === 0)
      ) {
        // Nothing torn down here: the break backstop cleans up once `breakDetails` commits,
        // so a transient Un-Register during a network flap never destroys a live call.
        return false;
      }
      if (
        (!!breakDetailsRef.current || callStatusRef.current) &&
        message?.event_type === 'EXTENSION_RINGING'
      ) {
        return false;
      }
      // Late/duplicate/out-of-order HANGUPs from earlier calls must not wrap the live call.
      // utils/callEventGuard.js decides; it still includes the original stale-uuid rule.
      const event = normalizeCallEvent(message);
      const previousState = callGuardRef.current.state;
      const verdict = decideEvent({
        event,
        guardState: previousState,
        currentUuids: currentCallUuidsRef.current,
        seenUuids: seenCallUuidsRef.current,
        mediaLiveness: getMediaLiveness(),
        forceAccept
      });

      if (verdict.decision !== DECISION.ACCEPT) {
        logGuardEvent(verdict.decision, {
          reason: verdict.reason,
          eventType: event.eventType,
          callType: event.callType,
          uuid: event.uuid,
          guardState: previousState,
          callId: callIdRef.current,
          mediaLiveness: getMediaLiveness()
        });
      }

      if (verdict.decision === DECISION.DROP) return false;
      if (verdict.decision === DECISION.HOLD) {
        holdHangup(message);
        return false;
      }
      // An accepted teardown supersedes anything still being held for this call.
      if (event.isHangupSignal) clearPendingHangup();
      // PASS keeps side effects but must not let a null status wipe a live ON CALL.
      const suppressStatus = verdict.decision === DECISION.PASS;
      callGuardRef.current = { state: verdict.nextState };

      // Every accepted event claims its uuid for the current call (originate and switch uuids
      // can differ). The set only grows mid-call and never resets from IDLE: a manual call's
      // originate uuid is seeded while IDLE, and evicting it would strand the call ON CALL.
      if (!event.isHangupSignal && event.uuid) {
        const supersedesPreviousCall =
          previousState === GUARD_STATE.WRAPUP && !currentCallUuidsRef.current.has(event.uuid);
        if (supersedesPreviousCall) currentCallUuidsRef.current = new Set();
        markCallUuidCurrent(event.uuid);
      }
      dispatch(onSetCallType(message?.call_type));
      if (message?.call_type === 'HANGUP') {
        dispatch(setCallId(message?.origination_uuid));
        dispatch(setCurrentCallDetail(message));
      }
      const userProcessType = user?.process_details?.type;
      const isIncomingManualCall =
        userProcessType === 'InboundAndManual' ||
        userProcessType === 'BlendedAndPredictive' ||
        userProcessType === 'BlendedAndPreview' ||
        userProcessType === 'BlendedAndProgressive';
      if (
        process.env.REACT_APP_INCOMING_SCREEN === 'true' &&
        isIncomingManualCall &&
        message?.call_type !== 'incoming' &&
        message?.event_type === 'EXTENSION_RINGING'
      ) {
        console.log('Manual Call Answer....');
        answerCall();
      }
      if (
        (message?.call_type === 'predictive' ||
          message?.call_type === 'incoming' ||
          message?.call_type === 'voicebroadcast' ||
          (message?.call_type === 'manual' &&
            window.self !== window.top &&
            message?.event_type !== 'ANSWER')) &&
        (message?.event_type === 'ANSWER' || message?.event_type === 'EXTENSION_RINGING')
      ) {
        // New call: clear the previous call's CRM details. Guarded on the uuid changing,
        // because RINGING and ANSWER of the same call both reach here.
        if (message?.origination_uuid !== callIdRef.current) {
          dispatch(removeCrmDetails());
        }
        dispatch(setCallId(message?.origination_uuid));
        // Update the ref now; the syncing effect runs after commit, too late for a HANGUP.
        callIdRef.current = message?.origination_uuid;
        markCallUuidCurrent(message?.origination_uuid);
        dispatch(setCallingModalOpen(true));
        dispatch(setCurrentCallDetail(message));
        getCrmDetails(message);
        dispatch(setCallDialSec(0));
        dispatch(
          setScreenType(
            message?.call_type === 'incoming' &&
              message?.event_type === 'EXTENSION_RINGING' &&
              process.env.REACT_APP_INCOMING_SCREEN === 'true'
              ? 'INCOMING'
              : 'LIVE_CALL'
          )
        );
        if (
          (message?.call_type === 'incoming' &&
            message?.event_type === 'EXTENSION_RINGING' &&
            process.env.REACT_APP_INCOMING_SCREEN === 'true') ||
          (window.self !== window.top && message?.event_type === 'EXTENSION_RINGING')
        ) {
          console.log('message?.destination_number', message?.destination_number);
          setNumber(message?.destination_number);
          dispatch(setCallNumber(message?.destination_number));
        }
      }
      const callType =
        message?.event_type === 'EXTENSION_RINGING'
          ? message?.call_type === 'predictive'
            ? 'ON CALL'
            : 'RINGING'
          : message?.event_type === 'DIALING'
            ? 'DIALING'
            : message?.event_type === 'ANSWER'
              ? 'ON CALL'
              : message?.event_type === 'HANGUP'
                ? 'WRAPUP'
                : null;
      // Unmapped events (HOLD, BRIDGE, transfer...) keep their side effects but must not
      // wipe a live ON CALL with a null status.
      if (suppressStatus) return;
      if (callStatus !== callType) {
        handleTimeReset();
        if (callType === 'WRAPUP') {
          if (callStatus) playBeep();
        } else if (
          // Manual: beep when the far end answers (ON CALL); the agent already knows it rings.
          // Others: beep on the first live state only, not again on RINGING -> ON CALL.
          (message?.call_type === 'manual'
            ? callType === 'ON CALL'
            : (callType === 'RINGING' || callType === 'ON CALL') && callStatus !== 'RINGING') &&
          (message?.call_type === 'predictive' ||
            message?.call_type === 'incoming' ||
            message?.call_type === 'voicebroadcast' ||
            message?.call_type === 'manual') &&
          !isRingtonePlaying()
        ) {
          playBeep();
        }
      }
      dispatch(onSetCallStatus(callType));
      if (callType) {
        dispatch(setSelectBreakType(null));
      }
    };

    const handleConnect = () => {
      console.log('Socket connected: ', socket.id);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    const handleReconnect = (attemptNumber) => {
      console.log('Socket reconnected after attempt ', attemptNumber);
      socket.emit('identify', { user_id: user.user_id, type: user?.type });
    };

    // Always the freshest handler for timers and the media-ended subscription.
    handleMessageRef.current = handleMessage;

    socket.on('connect', handleConnect);
    socket.on('reconnect', handleReconnect);
    socket.on('server_restart', handleServerRestart);
    socket.on('sendMessage', handleMessage);

    return () => {
      socket.off('server_restart', handleServerRestart);
      socket.off('sendMessage', handleMessage);
      // Detach only our handlers — a bare socket.off('connect') wipes every listener on
      // the shared socket, and this effect re-runs on every break change.
      socket.off('connect', handleConnect);
      socket.off('reconnect', handleReconnect);
    };
  }, [user, callStatus, breakDetails, sipStatus, isMini, window.self !== window.top]);

  const getLogDetails = async (data) => {
    try {
      await dispatch(crmInfoLog(data)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onProgressiveCall = async () => {
    // Until the first socket event, the softphone is the only proof a progressive call is
    // already up — ticking again would dial the agent a second time.
    const invitation = incomingInvitationRef.current;
    const ringing = !!invitation && invitation.state !== SessionState.Terminated;
    if (ringing || getMediaLiveness() === 'live') return;
    try {
      const response = await dispatch(progressiveCall()).unwrap();
      if (response && response?.data) {
        // A progressive dial is always a fresh call — clear the prior call's details.
        dispatch(removeCrmDetails());
        dispatch(setCallId(response?.data?.origination_uuid));
        callIdRef.current = response?.data?.origination_uuid;
        markCallUuidSeen(response?.data?.origination_uuid);
        dispatch(setCurrentCallDetail(response?.data));
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isMini) return;
    if (
      user &&
      user?.process_details &&
      (user?.process_details?.type === 'OutboundAndProgressive' ||
        user?.process_details?.type === 'BlendedAndProgressive') &&
      !callStatus &&
      !breakType
    ) {
      const apiRefresh = Number(user?.process_details?.ProgressiveDialingSec) || 5;

      const shouldRegister = Number(user.web_phone) === 0 ? sipStatus === 'Register' : true;

      if (shouldRegister) {
        if (!intervalRef.current) {
          intervalRef.current = setInterval(() => {
            onProgressiveCall();
          }, apiRefresh * 1000);
        }
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [user, callStatus, breakType, sipStatus, isMini]);

  // Progressive calls carry no number, so show the CRM's primary number once its unique_id
  // matches this call. Once per call only; skipped embedded (CallInProgress doesn't mask it).
  useEffect(() => {
    if (isMini || isEmbedded) return;
    if (callType !== 'progressive') return;
    if (!callStatus || callStatus === 'WRAPUP') return;
    const uniqueId = CurrentCallDetail?.unique_id;
    if (!uniqueId || crmDetails?.[4]?.unique_id !== uniqueId) return;
    if (progressiveNumberFilledRef.current === uniqueId) return;
    const primaryNumber = crmDetails?.[1]?.crmDetails?.primaryNumber;
    if (!primaryNumber) return;
    progressiveNumberFilledRef.current = uniqueId;
    dispatch(setCallNumber(primaryNumber));
  }, [crmDetails, callStatus, callType, CurrentCallDetail, isMini]);

  const manualDialingOn = async () => {
    try {
      await dispatch(manualDialing()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (breakType && breakType.name === 'OUTGOING') {
      manualDialingOn();
    }
  }, [breakType]);

  const getBreaksList = async () => {
    try {
      const payload = { process_id: user?.process_details?.process_id };
      await dispatch(breaksListGet(payload)).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    getBreaksList();
  }, [user]);

  const onUnregisterCall = async () => {
    try {
      await dispatch(setUnregisterCall()).unwrap();
    } catch (error) {
      console.log('error', error);
    }
  };

  const onSelectBreak = async (data, type) => {
    const isBreakIn = type === 'in';
    const payload = {
      break_id: data?.id,
      break_name: data?.name,
      type: type
    };

    setIsBreakLoading(true);

    // Latch the break before any await: the switch drops the agent now, but `breakDetails`
    // lands two requests later, and the HANGUP in between must not become WRAPUP + CRM.
    if (isBreakIn) {
      setBreakPending(true);
      breakDetailsRef.current = payload;
    }

    try {
      if (isBreakIn) {
        await onUnregisterCall();
      }
      window.__SIP_MANUAL_LOGOUT__ = true;
      reconnectInProgressRef.current = true;
      clearRegisterRetry();
      await dispatch(selectBreak(payload)).unwrap();
      dispatch(setBreakDetails(isBreakIn ? payload : null));
      // Update the ref now; the syncing effect runs after commit, too late for a live message.
      breakDetailsRef.current = isBreakIn ? payload : null;
      if (!isBreakIn) {
        setBreakPending(false);
      }
      dispatch(
        setSelectBreakType(isBreakIn ? { id: data?.id, name: data?.name, color: 'warning' } : null)
      );
      handleTimeReset();
      if (pauseMode === 0) {
        onPauseModeChange(1);
      }
    } catch (error) {
      // Break-in failed: release the latch or the agent stays deaf to calls with no break to
      // end. A failed break-out keeps it — the agent is still on break.
      if (isBreakIn) {
        setBreakPending(false);
        breakDetailsRef.current = breakDetails;
      }
      console.log('error', error);
    } finally {
      setIsBreakLoading(false);
    }
  };

  // Single gate for every auto-dial (follow-ups, redial timers, persisted `isDialNumber`):
  // on break, drop the request — by the time the break ends the number may be stale.
  useEffect(() => {
    if (isMini) return;
    if (isOnBreakNow()) {
      if (isDialNumber) dispatch(setIsDialNumber(null));
      return;
    }
    if (isDialNumber) {
      dialNumber(isDialNumber);
    }
  }, [isDialNumber, isMini]);

  const dialNumber = async (number) => {
    const numberType = dialNumberType;
    setNumber(number);
    dispatch(setCallNumber(number));
    dispatch(setIsReDial(false));
    dispatch(setIsDialNumber(null));
    dispatch(setDialNumberType(null));
    try {
      const payload = {
        destination_number: number,
        ...(numberType ? { number_type: numberType } : {})
      };
      const response = await dispatch(manualCall(payload)).unwrap();
      if (response && response?.data) {
        // A manual dial is always a fresh call — clear the prior call's details.
        dispatch(removeCrmDetails());
        dispatch(setCallDialSec(0));
        dispatch(setCallId(response?.data?.origination_uuid));
        callIdRef.current = response?.data?.origination_uuid;
        markCallUuidSeen(response?.data?.origination_uuid);
        dispatch(setCurrentCallDetail(response?.data));
        // Embedded: show the destination number from the manual call response.
        if (window.self !== window.top && response?.data?.destination_number) {
          setNumber(response.data.destination_number);
          dispatch(setCallNumber(response.data.destination_number));
        }
        getCrmDetails(response?.data);
        dispatch(setScreenType('LIVE_CALL'));
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  // Thin wrapper over utils/crmLanding: serializes attempts and latches the landed call so
  // the watchdog knows when to stop.
  const getCrmDetails = async (data) => {
    if (crmFetchInFlightRef.current) return false;
    crmFetchInFlightRef.current = true;
    try {
      const landed = await fetchCrmAndLand({
        dispatch,
        navigate,
        data,
        log: (message, message_data) =>
          getLogDetails({
            user_id: user.user_id,
            message,
            'socket.id': socket.id,
            message_data
          })
      });
      if (landed) {
        landedCallIdRef.current = callIdRef.current;
      }
      return landed;
    } finally {
      crmFetchInFlightRef.current = false;
    }
  };

  // Pull CRM info on demand, then hand off to getCrmDetails so the CRM screen renders as usual.
  const fetchManualCrmInfo = async () => {
    try {
      const response = await dispatch(manualCrmInfo()).unwrap();
      const data = response?.data;
      if (!Array.isArray(data)) return;

      // If the login session_id no longer matches socket.id (e.g. after a reconnect),
      // re-identify so server-side call events keep reaching this socket.
      const session_id = data.find((item) => item?.user_login_details)?.user_login_details
        ?.session_id;
      if (session_id && session_id !== socket.id) {
        socket.emit('identify', { user_id: user.user_id, type: user?.type });
      }

      const crm_id = data.find((item) => item?.crmDetails)?.crmDetails?.Crms_id;
      const unique_id = data.find((item) => item?.unique_id)?.unique_id;

      if (unique_id) {
        getCrmDetails({ crm_id, unique_id });
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  // Clear crmDetails rehydrated by a reload with no live call, so `!crmDetails` stays truthful.
  useEffect(() => {
    if (isMini) return;
    if (!callIdRef.current) {
      dispatch(removeCrmDetails());
    }
  }, []);

  // CRM-landing watchdog: retries while the call is live, stops once the agent has landed
  // on the CRM for this call — a deliberate navigation away afterwards is never fought.
  useEffect(() => {
    if (isMini) return;
    if (!callId || !callStatus || callStatus === 'WRAPUP' || breakDetails) return;
    if (landedCallIdRef.current === callId) return;
    if (!CurrentCallDetail?.unique_id) return;

    const tick = () => {
      if (isOnBreakNow()) return;
      if (landedCallIdRef.current === callIdRef.current) return;
      // Some other path already landed us on the CRM screen — latch and stop.
      const path = window.location.pathname;
      if (path === '/crm' || path === '/call-in-progress') {
        landedCallIdRef.current = callIdRef.current;
        return;
      }
      // getCrmDetails serializes itself, so an overlapping tick is simply skipped.
      getCrmDetails(CurrentCallDetail);
    };

    tick();
    const watchdog = setInterval(tick, 5000);
    return () => clearInterval(watchdog);
  }, [callId, callStatus, breakDetails, isMini, CurrentCallDetail]);

  const onIdleSet = async () => {
    // `setIdleCall` marks the agent available server-side, cancelling their break, and timers
    // can call this mid-break. BreakModal has its own onIdleSet for the real break-out.
    if (isOnBreakNow()) return;
    try {
      await dispatch(setIdleCall()).unwrap();
      setWrapsec(0);
      dispatch(setCallId(null));
      dispatch(setCurrentCallDetail(null));
      dispatch(setCallingModalOpen(user?.manualcall ? true : false));
      dispatch(setCallDialSec(null));
      dispatch(setSidebarType('main'));
      dispatch(setCallEnd(false));
      handleTimeReset();
      dispatch(onSetCallStatus(null));
      dispatch(onSetCallType(null));
      dispatch(setHoldTime(0));
      dispatch(setHoldStartTime(null));
      incomingInvitationRef.current = null;
      if (location.pathname !== '/chat') {
        navigate('/dashboard');
      }
    } catch (error) {
      console.log('error', error);
    }
  };

  useEffect(() => {
    // Last-resort force-idle for a call stuck in WRAPUP; must sit after the configured
    // auto-wrapup window so a long autoWrapup is not cut short.
    const autoWrapUp = Number(user?.process_details?.autoWrapup) || 10;
    const idleFallbackThreshold = Math.max(60, autoWrapUp + 30);
    // Not on break: `setIdleCall` would cancel the break server-side.
    if (
      callStatus === 'WRAPUP' &&
      wrapsec > idleFallbackThreshold &&
      !isEmbedded &&
      !isMini &&
      !isOnBreakNow()
    ) {
      onIdleSet();
    }
  }, [callStatus, wrapsec, isEmbedded, isMini]);

  const renderScreen = () => {
    switch (screenType) {
      case 'LIVE_CALL':
        return <DefaultLiveCall setNumber={setNumber} number={number} />;

      case 'INCOMING':
        return (
          <IncomingCallScreen
            setNumber={setNumber}
            number={number}
            answerCall={answerCall}
            stopRingtone={stopRingtone}
            incomingInvitationRef={incomingInvitationRef}
          />
        );

      case 'DTMF':
        return <DtmfScreen />;

      case 'TRANSFER':
        return <TransferScreen number={number} />;

      case 'AGENT_TRANSFER':
        return <AgentTransferScreen number={number} />;

      case 'ATTENDED_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'BLIND_TRANSFER':
        return <UserTransferScreen number={number} />;

      case 'USER_CALL':
        return <UserCallScreen />;

      case 'TRANSFER_HANGUP':
        return <TransferHangupScreen number={number} />;

      case 'CONFERENCE':
        return <ConferenceScreen number={number} />;

      case 'CONFERENCE_LIVE':
        return <ConferenceLiveScreen number={number} setNumber={setNumber} />;

      default:
        return callStatus === 'WRAPUP' ? (
          <div className="input-container">
            <div className="d-flex flex-column gap-2 justify-content-center align-items-center">
              <div
                className="bg-violet rounded-circle d-flex justify-content-center align-items-center cursor-pointer"
                style={{ height: '70px', width: '70px' }}
                onClick={() => {
                  dispatch(setIsReDial(true));
                  socket.emit('identify-redial', { user_id: user.user_id, type: user?.type });
                  dispatch(setCallEnd(true));
                  setNumber('');
                  dispatch(setCallNumber(''));
                  dispatch(setScreenType(null));
                }}>
                <Icon.PhoneForwarded className="text-white" size={30} />
              </div>
              <div className="text-white fs-5 fw-bold">{t('redial')}</div>
            </div>
          </div>
        ) : (
          <DialPadScreen
            number={number}
            setNumber={setNumber}
            setDuration={handleTimeReset}
            duration={duration}
            onIdleSet={onIdleSet}
          />
        );
    }
  };

  const renderCallStatus = () => {
    const callStatusClasses = {
      WRAPUP: 'bg-violet',
      RINGING: 'bg-blue',
      DIALING: 'bg-purple',
      'ON CALL': 'bg-green'
    };

    const statusClass =
      callStatusClasses[callStatus] || (breakType?.color ? `bg-${breakType.color}` : 'bg-info');
    return (
      <div className={`${statusClass} py-1 w-100 text-center text-white rounded-2 idle-time`}>
        <span className="text-uppercase">
          {callStatus
            ? t(callStatus)
            : (breakType?.name && breakType?.name === 'OUTGOING'
                ? t('OUTGOING')
                : breakType?.name) || t('IDLE')}
        </span>{' '}
        <span>
          <Timer duration={duration} setDuration={setDuration} />
        </span>
      </div>
    );
  };

  const renderBreakStatus = () => {
    if (callStatus && callStatus !== 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            onClick={() => {
              getCrmDetails(CurrentCallDetail);
            }}>
            {t('refetchDetails')}
          </Button>
        </div>
      );
    }
    if (callStatus === 'WRAPUP') {
      return (
        <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={location.pathname === '/crm'}
            onClick={() => {
              onIdleSet();
            }}>
            {t('setIdle')}
          </Button>
        </div>
      );
    }
    if ((breakType && breakType?.name === 'OUTGOING') || callStatus !== null) {
      return false;
    }
    return (
      <div className={`px-4 break-container ${!user?.manualcall && !callId && 'pt-0'}`}>
        {breakType ? (
          <Button
            caret
            size="sm"
            className="ready-btn w-100"
            disabled={isBreakLoading}
            onClick={() => {
              onSelectBreak(breakType, 'out');
            }}>
            {isBreakLoading ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-1"
                  role="status"
                  aria-hidden="true"
                />
                {t('ready')}...
              </>
            ) : (
              t('ready')
            )}
          </Button>
        ) : (
          <ButtonDropdown
            className="break-button-dropdown"
            isOpen={isOpen}
            toggle={() => !isBreakLoading && setIsOpen(!isOpen)}>
            <DropdownToggle className="break-button" caret size="sm" disabled={isBreakLoading}>
              {isBreakLoading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-1"
                    role="status"
                    aria-hidden="true"
                  />
                  {t('break')}...
                </>
              ) : (
                t('break')
              )}
            </DropdownToggle>
            <DropdownMenu>
              {breaksList.map((val, index) => {
                return (
                  <DropdownItem
                    key={index}
                    onClick={() => {
                      onSelectBreak(val, 'in');
                    }}>
                    {val?.name}
                  </DropdownItem>
                );
              })}
            </DropdownMenu>
          </ButtonDropdown>
        )}
      </div>
    );
  };

  // Call Agent and Fetch CRM Info share one row under the break button, half each.
  const renderDialActions = () => {
    // Call Agent belongs to the dial pad, so it shows only while the dial pad is the screen.
    const showCallAgent = !screenType && callStatus !== 'WRAPUP';
    const showFetchCrm = !callStatus;
    if (!showCallAgent && !showFetchCrm) return null;

    return (
      <div className="px-4 dial-action-row">
        {showCallAgent && (
          <Button
            size="sm"
            className="ready-btn"
            onClick={() => dispatch(setScreenType('USER_CALL'))}>
            {t('callAgent')}
          </Button>
        )}
        {showFetchCrm && (
          <Button
            size="sm"
            className="ready-btn"
            title={t('fetchCrmInfo')}
            onClick={() => fetchManualCrmInfo()}>
            {/* Half width only fits the short label; alone it has room for the full one. */}
            {showCallAgent ? t('crmInfo') : t('fetchCrmInfo')}
          </Button>
        )}
      </div>
    );
  };

  const renderProcessDropdown = () => {
    return user?.multi_process_data?.length > 0 ? (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen((prev) => !prev)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>

          <DropdownMenu>
            {user.multi_process_data.map((process) => (
              <DropdownItem
                key={process.id}
                active={process.id === user?.process_details?.process_id}
                onClick={() => {
                  if (
                    process.id !== user?.process_details?.process_id &&
                    callStatus === null &&
                    !breakType
                  ) {
                    setIsProcessModalOpen({ isOpen: true, selectedProcess: process });
                  }
                }}>
                {process.name}
              </DropdownItem>
            ))}
          </DropdownMenu>
        </ButtonDropdown>
      </div>
    ) : (
      <div className="px-4">
        <ButtonDropdown
          className="break-button-dropdown"
          isOpen={isConVoxOpen}
          toggle={() => setIsConVoxOpen(!isConVoxOpen)}>
          <DropdownToggle className="break-button" caret size="sm">
            {user?.process_details?.name}
          </DropdownToggle>
          <DropdownMenu>{user?.process_details?.name}</DropdownMenu>
        </ButtonDropdown>
      </div>
    );
  };

  return (
    <React.Fragment>
      {!user?.manualcall && !callId ? (
        <div className="predictive-container">
          <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
          {renderProcessDropdown()}
          <div className="px-4 py-2">{renderCallStatus()}</div>
          {renderBreakStatus()}
        </div>
      ) : (
        <div className="calling-sidebar p-3 position-relative">
          <div
            className="position-absolute cx-chrome shadow-sm rounded-5 top-50 border-white text-white d-flex justify-content-center align-items-center callpase-icon"
            onClick={() => dispatch(setCallingModalOpen(false))}>
            <Icon.ChevronRight />
          </div>
          <div className="shadow-sm dail-page-container rounded-4 py-3">
            <div className="pb-2 text-center text-light fs-6">{getCurrentDateTime(timezone)}</div>
            {renderProcessDropdown()}
            <div className="px-4 py-2">{renderCallStatus()}</div>
            {renderScreen()}
            {renderBreakStatus()}
            {renderDialActions()}
          </div>
        </div>
      )}
      <MicrophonePermissionModal
        isOpen={showMicModal}
        permissionState={micPermission}
        onEnableClick={requestMicrophonePermission}
        onLogout={onLogout}
      />
      {!isMini && (
        <SipConnectionModal
          isOpen={isSipAlertOpen}
          reason={sipIssue}
          sipStatus={sipStatus}
          isRetrying={isSipRetrying}
          onRetry={retrySipRegistration}
          onLogout={onLogout}
        />
      )}
    </React.Fragment>
  );
};

CallingSidebar.propTypes = {
  setIsProcessModalOpen: PropTypes.func,
  isMini: PropTypes.bool
};

export default CallingSidebar;
